import React from "react";
import { useSelector } from "react-redux";
import {
  Paper,
  Table,
  TableContainer,
  CircularProgress,
  Typography,
  Box,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CommonTableHeaders from "./TableHeaders/CommonTableHeaders";
import RequestsTableBody from "./TableBodys/RequestsTableBody";

interface GameInfo {
  id: string;
  [key: string]: any;
}

interface RequestsTableProps {
  gameInfo: GameInfo;
  setModalDets: (dets: any) => void;
}

const RequestsTable: React.FC<RequestsTableProps> = ({
  gameInfo,
  setModalDets,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const receivedRequests = useSelector(
    (state: any) => state.requests.receivedRequests,
  );

  const getNumGameReqs = () => {
    const reqIds = Object.keys(receivedRequests);
    const reqArr = reqIds
      .map((reqId) => receivedRequests[reqId])
      .filter((req) => req.gameId == gameInfo.id && req.status === "pending");
    return reqArr.length;
  };

  if (!receivedRequests) {
    return (
      <TableContainer
        component={Paper}
        sx={{
          ...theme.table?.defaultContainer,
          textAlign: "center",
          margin: "auto",
          padding: "auto",
          borderRadius: "20px",
        }}
        elevation={5}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 200,
            flexDirection: "column",
            gap: 2,
            p: 3,
          }}
        >
          <CircularProgress color="primary" size={40} thickness={4} />
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: "center" }}
          >
            Loading requests...
          </Typography>
        </Box>
      </TableContainer>
    );
  }

  if (!getNumGameReqs()) {
    return (
      <TableContainer
        component={Paper}
        sx={{
          ...theme.table?.defaultContainer,
          textAlign: "center",
          margin: "auto",
          padding: "auto",
          borderRadius: "20px",
        }}
        elevation={5}
      >
        <Box
          sx={{
            textAlign: "center",
            py: 6,
            px: 3,
            minHeight: 200,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ fontWeight: 400 }}
          >
            No pending requests
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ opacity: 0.8 }}
          >
            New requests will appear here when players join your game
          </Typography>
        </Box>
      </TableContainer>
    );
  }

  return (
    <TableContainer
      component={Paper}
      sx={{
        ...theme.table?.defaultContainer,
        textAlign: "center",
        margin: "auto",
        padding: "auto",
        borderRadius: "20px",
        overflow: "hidden",
        maxHeight: isMobile ? 400 : 600,
        "& .MuiTableCell-root": {
          borderBottom: `1px solid ${theme.palette.divider}`,
        },
      }}
      elevation={5}
    >
      <Table
        stickyHeader
        size={isMobile ? "small" : "medium"}
        sx={{
          "& .MuiTableHead-root": {
            "& .MuiTableCell-root": {
              bgcolor: "primary.main",
              color: "primary.contrastText",
              fontWeight: 500,
              borderBottom: "none",
            },
          },
        }}
      >
        <CommonTableHeaders />
        <RequestsTableBody
          setModalDets={setModalDets}
          gameId={gameInfo.id}
        />
      </Table>
    </TableContainer>
  );
};

export default RequestsTable;
